import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import confetti from 'canvas-confetti';
import { ENDPOINTS, getAuthHeaders, handleAuthError } from './config';

// Lines, wins and prize state are decided by the backend from item IDs
// (see BINGO_LINE_DEFINITIONS in bingo_system.py) and read off the API
// response.  The UI deliberately keeps no copy of that logic: the old copy
// indexed a 5x5 line table by array position, so a reordered or short
// response celebrated lines the board had never actually awarded.
const EMPTY_STATS = Object.freeze({
    completed_count: 0,
    total_count: 25,
    lines_completed: 0,
    lines_detail: [],
    is_full_house: false,
    board_is_valid: true,
    board_problems: []
});

const BINGO_HEADER = ['B', 'I', 'N', 'G', 'O'];

// The centre square. Mirrors FREE_ITEM_ID in bingo_system.py, which is the
// authority: it is seeded completed with the card and both /api/bingo/update
// and /api/bingo/undo now refuse it outright. Rendering it as an enabled
// button put a control in the tab order whose only possible outcome was a
// rejected request -- and, before the backend refused, a request that flipped
// the middle of the board to pending.
const FREE_ITEM_ID = 12;

// Everything a keyboard can land on inside a dialog. Used to keep Tab and
// Shift+Tab inside an aria-modal dialog rather than walking the board behind it.
const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(', ');

// Check if reduced motion is preferred
const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Fire confetti with accessibility check
const fireConfetti = (options = {}) => {
    if (prefersReducedMotion()) return;
    confetti({
        particleCount: 50,
        spread: 60,
        origin: { y: 0.6 },
        colors: ['#10b981', '#34d399', '#fbbf24', '#f97316'],
        ...options
    });
};

// Big celebration for line completion
const fireBigCelebration = () => {
    if (prefersReducedMotion()) return;
    const count = 100;
    const defaults = { origin: { y: 0.7 }, colors: ['#10b981', '#34d399', '#fbbf24', '#f97316', '#ec4899'] };

    confetti({ ...defaults, particleCount: count, spread: 100, angle: 60, origin: { x: 0 } });
    confetti({ ...defaults, particleCount: count, spread: 100, angle: 120, origin: { x: 1 } });
};

// Grand finale for full board
const fireGrandFinale = () => {
    if (prefersReducedMotion()) return;
    const duration = 3000;
    const end = Date.now() + duration;

    const interval = setInterval(() => {
        if (Date.now() > end) {
            clearInterval(interval);
            return;
        }
        confetti({
            particleCount: 30,
            angle: 60,
            spread: 55,
            origin: { x: 0 },
            colors: ['#10b981', '#fbbf24', '#ec4899', '#8b5cf6']
        });
        confetti({
            particleCount: 30,
            angle: 120,
            spread: 55,
            origin: { x: 1 },
            colors: ['#10b981', '#fbbf24', '#ec4899', '#8b5cf6']
        });
    }, 150);
};

const PLAYER_THEMES = {
    kate: {
        label: "Kate's Bingo",
        shortLabel: 'Kate',
        emoji: '☕️',
        tabActive: 'bg-rose-200 text-rose-900 ring-4 ring-rose-100',
        badge: 'bg-rose-100 text-rose-700',
        border: 'border-rose-200',
        tilePending: 'border-rose-200/80 bg-white hover:border-rose-400 hover:bg-rose-50',
    },
    nav: {
        label: "Nav's Bingo",
        shortLabel: 'Nav',
        emoji: '🚀',
        tabActive: 'bg-sky-200 text-sky-900 ring-4 ring-sky-100',
        badge: 'bg-sky-100 text-sky-700',
        border: 'border-sky-200',
        tilePending: 'border-sky-200/80 bg-white hover:border-sky-400 hover:bg-sky-50',
    },
};

// Curated short labels for tiles. The full challenge text is always kept for the
// detail sheet, the tooltip and the accessible name -- this map only trims what
// is *rendered inside* a 5x5 tile so it stays legible at phone width.
const TILE_LABELS = {
    // Kate
    'Learn 3 Punjabi phrases': '3 Punjabi phrases',
    'Go to an MLB game': 'MLB game',
    'Make latte art': 'Latte art',
    'Meal prep for 1 week': 'Meal prep 1 week',
    'Go to a Whitecaps game': 'Whitecaps game',
    'Ride the Stanley Park Train': 'Stanley Park train',
    'Attend a CME conference': 'CME conference',
    'Cook a new meal / new recipe': 'Cook new recipe',
    'See the Northern Lights': 'Northern Lights',
    'Go to an NFL game': 'NFL game',
    'Read/listen to 10 books': 'Read 10 books',
    'Squat 110 lbs x 12 reps': 'Squat 110lb ×12',
    'Take a dance class': 'Dance class',
    'Mexico trip with Nav': 'Mexico with Nav',
    'Buy a new couch': 'New couch',
    'Buy a new bed': 'New bed',
    'Duolingo 1500-day streak': 'Duolingo 1500d',
    'Perform a guitar song for Nav': 'Guitar song for Nav',
    'Go to a World Cup game': 'World Cup game',
    'Go to Krauseberry Farms': 'Krauseberry Farms',
    // Nav
    'Do 15 paper trades': '15 paper trades',
    'Apply to 15 jobs': 'Apply 15 jobs',
    'Watch an NFL game': 'NFL game',
    'Build bucket list app on Navjeev.com': 'Bucket list app',
    'Make 3 "cool" reels': '3 cool reels',
    'Complete trading course': 'Trading course',
    'Move closer to Kate': 'Move near Kate',
    'Watch an MLB / Whitecaps': 'MLB / Whitecaps',
    'Learn how to create internal app at Meta': 'Internal app @ Meta',
    'Do 5 actual trades': '5 real trades',
    'Drive from BEL to VAN': 'BEL → VAN drive',
    'AI system for investment analysis': 'AI investing system',
    'One bucket list travel item': 'Bucket list trip',
    'Make a go-to cocktail': 'Go-to cocktail',
    '5RM 75kg bench press': 'Bench 75kg 5RM',
    'Automate 90% of TMBS': 'Automate TMBS 90%',
    'Watch a World Cup game': 'World Cup game',
};

const getTileLabel = (text) => TILE_LABELS[text] || text || '';

const TILE_TEXT_SIZES = [
    'text-[0.62rem] sm:text-[0.9rem]',
    'text-[0.55rem] sm:text-[0.84rem]',
    'text-[0.5rem] sm:text-[0.78rem]',
    'text-[0.46rem] sm:text-[0.72rem]',
];

// Tile text is sized by total length *and* by the longest single word: a word wider
// than the tile is what forces an ugly mid-word break, so it gets its own step down.
const getTileTextClass = (text) => {
    if (!text) return TILE_TEXT_SIZES[0];
    const longestWord = text.split(/\s+/).reduce((max, word) => Math.max(max, word.length), 0);
    let step = 0;
    if (text.length > 30) step = 3;
    else if (text.length > 22) step = 2;
    else if (text.length > 16) step = 1;

    if (longestWord >= 10) step = Math.max(step, 3);
    else if (longestWord >= 9) step = Math.max(step, 2);
    else if (longestWord >= 8) step = Math.max(step, 1);

    return TILE_TEXT_SIZES[Math.min(step, TILE_TEXT_SIZES.length - 1)];
};

// One stat inside the compact summary row. Short label on phones, full on desktop.
const SummaryStat = ({ shortLabel, label, value }) => (
    <div className="flex items-baseline gap-1.5">
        <dt className="text-[0.6rem] font-semibold uppercase tracking-[0.14em] text-stone-400 sm:text-xs">
            <span className="sm:hidden">{shortLabel}</span>
            <span className="hidden sm:inline">{label}</span>
        </dt>
        <dd className="text-base font-semibold leading-none text-stone-900 sm:text-xl">{value}</dd>
    </div>
);

const BingoInterface = () => {
    const navigate = useNavigate();
    const [activePerson, setActivePerson] = useState('kate');
    const [bingoData, setBingoData] = useState({ kate: { items: [] }, nav: { items: [] } });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [confirmDate, setConfirmDate] = useState(new Date().toISOString().split('T')[0]);
    // ONE dialog state, not two booleans. There used to be `showModal` and
    // `showUndoModal`, and every close path had to remember to clear the right
    // one: "Update Date" lives in the manage sheet but shares its handler with
    // the completion sheet's Confirm, so it cleared `showModal` only. The panel
    // still vanished (`selectedItem` went null) while `showUndoModal` stayed
    // true, which left the board inert, the page unscrollable and focus
    // stranded, with nothing on screen to say so. Two flags that must never
    // disagree are better modelled as one value that cannot.
    const [activeDialog, setActiveDialog] = useState(null); // null | 'complete' | 'manage'
    const [undoReason, setUndoReason] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [recentlyCompletedId, setRecentlyCompletedId] = useState(null);
    const [isModalAnimating, setIsModalAnimating] = useState(false);

    // The dialogs are aria-modal, which is a promise: while one is open the
    // board behind it must be neither clickable nor reachable by Tab or a
    // screen reader, and closing must hand focus back to the square that was
    // tapped -- otherwise a keyboard user is dumped at the top of the page.
    const boardRef = useRef(null);
    const dialogRef = useRef(null);
    const lastFocusedTileRef = useRef(null);
    const closeTimerRef = useRef(null);

    // The single source of truth, derived so it cannot disagree with what is
    // on screen: a dialog is open exactly when one is *rendered*, and a dialog
    // renders only with an item to act on. The inert / scroll-lock / focus
    // effects and the JSX below both read this, so the attributes come off at
    // the same instant the panel leaves the DOM -- whichever of `activeDialog`
    // or `selectedItem` is cleared first, and in whatever order.
    const openDialog = selectedItem ? activeDialog : null;
    const isDialogOpen = openDialog !== null;

    // Squares always render in card order, whatever order the API listed them in.
    const items = useMemo(
        () => [...(bingoData[activePerson]?.items || [])].sort((a, b) => a.id - b.id),
        [bingoData, activePerson]
    );

    // Authoritative board result from the server.
    const stats = bingoData[activePerson]?.stats || EMPTY_STATS;
    const completedLines = stats.lines_detail || [];
    const isFullBoard = Boolean(stats.is_full_house);
    const totalItems = stats.total_count || 25;
    // Counting ticked squares is order-independent, so the progress bar can
    // follow the optimistic update while lines and the win wait for the server.
    const completedCount = items.filter(i => i.status === 'completed').length;
    const progressPercent = Math.min((completedCount / totalItems) * 100, 100);
    const remainingCount = Math.max(totalItems - completedCount, 0);
    const boardProblems = stats.board_problems || [];
    const activeTheme = PLAYER_THEMES[activePerson];

    useEffect(() => {
        fetchBingoStatus();
    }, []);

    // The one way out. Every close path -- Escape, backdrop, Cancel, Confirm,
    // Update Date, Uncheck, and the failure branch of each -- goes through
    // here, so none of them can close "half way".
    const closeModal = useCallback(() => {
        setIsModalAnimating(false);
        if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
        closeTimerRef.current = setTimeout(() => setActiveDialog(null), 150);
    }, []);

    // Don't leave the exit-animation timer to fire into an unmounted tree.
    useEffect(() => () => {
        if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    }, []);

    // Handle Escape key to close modals
    useEffect(() => {
        if (!isDialogOpen) return undefined;
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') closeModal();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isDialogOpen, closeModal]);

    // Lock body scroll when modal is open
    useEffect(() => {
        if (isDialogOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => { document.body.style.overflow = 'unset'; };
    }, [isDialogOpen]);

    // Make the page behind an open dialog genuinely inert. `aria-hidden` alone
    // hides it from screen readers but leaves every tile clickable and
    // tabbable; `inert` alone still leaves it announced in older engines. Set
    // through the DOM because React 18 does not pass `inert` through JSX.
    // Declared BEFORE the focus effect so that on close the attribute is gone
    // by the time we try to focus a tile inside it.
    useEffect(() => {
        const board = boardRef.current;
        if (!board) return undefined;
        if (isDialogOpen) {
            board.setAttribute('inert', '');
            board.setAttribute('aria-hidden', 'true');
        } else {
            board.removeAttribute('inert');
            board.removeAttribute('aria-hidden');
        }
        return () => {
            board.removeAttribute('inert');
            board.removeAttribute('aria-hidden');
        };
    }, [isDialogOpen]);

    // Move focus into the dialog on open and give it back on close. The
    // heading rather than the date field, so opening a sheet on a phone does
    // not immediately throw up the native date picker.
    useEffect(() => {
        if (!isDialogOpen) return undefined;
        const heading = dialogRef.current?.querySelector('[data-dialog-heading]');
        (heading || dialogRef.current)?.focus();
        return () => {
            const tile = lastFocusedTileRef.current;
            if (tile && document.body.contains(tile)) tile.focus();
        };
    }, [isDialogOpen]);

    // Keep Tab and Shift+Tab inside the dialog. Focus starts on the heading,
    // which is tabindex="-1" and so is not itself in the cycle: index -1 means
    // "just entered", and wraps to whichever end the user is heading for.
    const handleDialogKeyDown = (e) => {
        if (e.key !== 'Tab') return;
        const dialog = dialogRef.current;
        if (!dialog) return;

        const focusables = Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR))
            .filter((el) => el.getClientRects().length > 0);
        if (focusables.length === 0) {
            e.preventDefault();
            return;
        }

        const index = focusables.indexOf(document.activeElement);
        if (e.shiftKey) {
            if (index <= 0) {
                e.preventDefault();
                focusables[focusables.length - 1].focus();
            }
        } else if (index === -1 || index === focusables.length - 1) {
            e.preventDefault();
            focusables[0].focus();
        }
    };

    const fetchBingoStatus = async () => {
        try {
            setLoading(true);
            const response = await fetch(ENDPOINTS.BINGO_STATUS, {
                headers: getAuthHeaders()
            });
            if (handleAuthError(response)) return;
            if (!response.ok) throw new Error('Failed to fetch data');
            const data = await response.json();
            setBingoData(data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching bingo status:', error);
            setError("Could not load bingo data. Please try again.");
            toast.error("Network error: Could not load data.");
            setLoading(false);
        }
    };

    const handleItemClick = (item, tileElement) => {
        // The FREE square is never editable; the backend rejects it too.
        if (item.id === FREE_ITEM_ID) return;
        // The guard that `disabled` used to provide -- see the tiles below for
        // why the attribute itself had to go.
        if (isSubmitting) return;

        // Remembered so every close path -- Escape, backdrop, Cancel, Confirm
        // -- can put focus back where the user left it.
        lastFocusedTileRef.current = tileElement || null;

        setSelectedItem(item);
        // Pre-fill date if available, else today
        setConfirmDate(item.date || new Date().toISOString().split('T')[0]);

        setActiveDialog(item.status === 'completed' ? 'manage' : 'complete');
        setTimeout(() => setIsModalAnimating(true), 10);
    };

    // Completed lines arrive from the server as canonical item IDs.
    const isInCompletedLine = (itemId) => {
        return completedLines.some(line => (line.items || []).includes(itemId));
    };

    const handleConfirmCompletion = async () => {
        if (!selectedItem) return;

        // Store previous state so a failed request can be rolled back.
        const previousData = JSON.parse(JSON.stringify(bingoData));

        // Optimistic Update
        const optimisticData = { ...bingoData };
        const itemIndex = optimisticData[activePerson].items.findIndex(i => i.id === selectedItem.id);
        if (itemIndex !== -1) {
            optimisticData[activePerson].items[itemIndex].status = 'completed';
            optimisticData[activePerson].items[itemIndex].date = confirmDate;
            setBingoData(optimisticData);
        }

        // Close whichever dialog invoked this. Both do: the completion sheet's
        // Confirm and the manage sheet's "Update Date" are the same request.
        closeModal();
        setIsSubmitting(true);

        try {
            const response = await fetch(ENDPOINTS.BINGO_UPDATE, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    person: activePerson,
                    item_id: selectedItem.id,
                    status: 'completed',
                    date: confirmDate
                })
            });

            if (handleAuthError(response)) return;
            if (response.ok) {
                const result = await response.json();
                setBingoData(result.data);

                // Track recently completed item for animation
                setRecentlyCompletedId(selectedItem.id);
                setTimeout(() => setRecentlyCompletedId(null), 600);

                // Celebrate only what the server actually awarded.
                const triggers = result.event?.triggers || {};

                if (triggers.full_house) {
                    // Grand finale for full board!
                    fireGrandFinale();
                    toast.success('FULL BOARD! You win the Grand Prize!', {
                        duration: 8000,
                        icon: '🏆',
                    });
                } else if (triggers.new_line_completed) {
                    // Line completion celebration
                    fireBigCelebration();
                    toast.success(
                        `BINGO! ${triggers.lines_total} line${triggers.lines_total === 1 ? '' : 's'} complete. Time for a <$50 gift!`,
                        { duration: 5000, icon: '🎉' }
                    );
                } else {
                    // Regular completion
                    fireConfetti();
                    toast.success('Item marked as complete!', { icon: '✅' });
                }
            } else {
                throw new Error('Server returned error');
            }
        } catch (error) {
            console.error('Error updating item:', error);
            setBingoData(previousData);
            toast.error('Failed to update status. Changes reverted.');
        } finally {
            // Success, HTTP error, thrown network error and the 401 early
            // return all land here. Closing again is idempotent and is what
            // makes "the dialog is closed" true on *every* exit, not just the
            // happy path -- a failed save that trapped the user would be worse
            // than a successful one, since they will certainly try again.
            setActiveDialog(null);
            setSelectedItem(null);
            setIsSubmitting(false);
        }
    };

    const handleConfirmUndo = async () => {
        if (!selectedItem) return;

        // Reason is now optional/auto-filled since email is disabled
        const reasonToSend = undoReason.trim() || "Manual removal via app";

        // Optimistic Update
        const previousData = JSON.parse(JSON.stringify(bingoData));
        const optimisticData = { ...bingoData };
        const itemIndex = optimisticData[activePerson].items.findIndex(i => i.id === selectedItem.id);
        if (itemIndex !== -1) {
            optimisticData[activePerson].items[itemIndex].status = 'pending';
            setBingoData(optimisticData);
        }

        closeModal();
        setIsSubmitting(true);

        try {
            const response = await fetch(ENDPOINTS.BINGO_UNDO, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    person: activePerson,
                    item_id: selectedItem.id,
                    reason: reasonToSend
                })
            });

            if (handleAuthError(response)) return;
            if (response.ok) {
                const result = await response.json();
                setBingoData(result.data);
                setUndoReason('');
                const triggers = result.event?.triggers || {};
                if (triggers.full_house_lost) {
                    toast('⚠️ Item reverted — the board is no longer a full house.', { duration: 6000 });
                } else if (triggers.lines_lost > 0) {
                    toast(
                        `⚠️ Item reverted — ${triggers.lines_lost} line${triggers.lines_lost === 1 ? '' : 's'} lost, ${triggers.lines_total} remaining.`,
                        { duration: 6000 }
                    );
                } else {
                    toast.success('⚠️ Item reverted.');
                }
            } else {
                throw new Error('Server returned error');
            }
        } catch (error) {
            console.error('Error undoing item:', error);
            setBingoData(previousData);
            toast.error('Failed to undo item. Changes reverted.');
        } finally {
            // Success, HTTP error, thrown network error and the 401 early
            // return all land here. Closing again is idempotent and is what
            // makes "the dialog is closed" true on *every* exit, not just the
            // happy path -- a failed save that trapped the user would be worse
            // than a successful one, since they will certainly try again.
            setActiveDialog(null);
            setSelectedItem(null);
            setIsSubmitting(false);
        }
    };

    if (error && !bingoData.kate.items.length) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-orange-50 gap-4">
                <p className="text-red-600 font-bold">{error}</p>
                <button
                    onClick={fetchBingoStatus}
                    className="px-4 py-2 bg-emerald-600 text-white rounded shadow"
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[radial-gradient(circle_at_top,_#fff6ec,_#f8f1e8_42%,_#efe7de_100%)] px-4 py-4 font-sans text-gray-800 sm:px-6">
            {/* Everything behind the dialogs. Marked inert while one is open. */}
            <div className="mx-auto max-w-5xl" ref={boardRef}>
                <section className="mb-3 overflow-hidden rounded-[24px] border border-white/70 bg-white/80 p-3 shadow-[0_28px_80px_rgba(120,53,15,0.10)] backdrop-blur sm:mb-5 sm:rounded-[32px] sm:p-6">
                    <div className="flex items-center gap-3 sm:items-start">
                        <button
                            onClick={() => navigate('/')}
                            className="inline-flex h-11 min-w-[44px] shrink-0 items-center justify-center rounded-full border border-stone-200 bg-white px-3 text-sm font-medium text-stone-600 transition hover:border-stone-300 hover:bg-stone-50 sm:px-4"
                            aria-label="Back to Home"
                        >
                            <span aria-hidden="true">←</span>
                            <span className="ml-2 hidden sm:inline">Back to Home</span>
                        </button>
                        <div className="min-w-0 max-w-2xl">
                            <div className={clsx('hidden rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] sm:inline-flex', activeTheme.badge)}>
                                Seasonal challenge board
                            </div>
                            <h1 className="text-xl font-semibold leading-tight tracking-tight text-orange-900 sm:mt-4 sm:text-5xl">Couples Bingo Challenge 💍</h1>
                            <p className="mt-3 hidden max-w-xl text-sm leading-6 text-stone-600 sm:block sm:text-base">
                                A more tactile board with clearer progress, roomier mobile tiles, and full milestone labels that stay readable at phone width.
                            </p>
                        </div>
                    </div>
                </section>

                <div className="mb-3 flex justify-center sm:mb-5">
                    <div role="tablist" className="flex w-full max-w-md gap-1.5 rounded-full bg-white/80 p-1.5 shadow-sm ring-1 ring-stone-200 sm:gap-3 sm:p-2">
                        <button
                            role="tab"
                            aria-selected={activePerson === 'kate'}
                            onClick={() => setActivePerson('kate')}
                            className={clsx(
                                "min-h-[44px] flex-1 rounded-full px-3 text-sm font-semibold transition-all focus:outline-none focus:ring-2 sm:px-5 sm:text-base",
                                activePerson === 'kate'
                                    ? PLAYER_THEMES.kate.tabActive
                                    : 'bg-white text-gray-500 hover:bg-stone-50'
                            )}
                        >
                            Kate's Bingo ☕️
                        </button>
                        <button
                            role="tab"
                            aria-selected={activePerson === 'nav'}
                            onClick={() => setActivePerson('nav')}
                            className={clsx(
                                "min-h-[44px] flex-1 rounded-full px-3 text-sm font-semibold transition-all focus:outline-none focus:ring-2 sm:px-5 sm:text-base",
                                activePerson === 'nav'
                                    ? PLAYER_THEMES.nav.tabActive
                                    : 'bg-white text-gray-500 hover:bg-stone-50'
                            )}
                        >
                            Nav's Bingo 🚀
                        </button>
                    </div>
                </div>

                {/* One compact summary row: who is playing, counts, line progress and the bar */}
                {!loading && (
                    <section
                        className={clsx('mb-3 rounded-[22px] border bg-white/85 p-3 shadow-sm backdrop-blur sm:mb-5 sm:rounded-[28px] sm:p-4', activeTheme.border)}
                        aria-label="Board progress summary"
                    >
                        <div className="flex items-center gap-2">
                            <span className={clsx('inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold sm:px-3 sm:text-sm', activeTheme.badge)}>
                                <span aria-hidden="true">{activeTheme.emoji}</span>
                                <span className="sm:hidden">{activeTheme.shortLabel}</span>
                                <span className="hidden sm:inline">{activeTheme.label}</span>
                            </span>
                            <dl className="flex flex-1 items-baseline justify-end gap-3 sm:gap-6">
                                <SummaryStat shortLabel="Done" label="Completed" value={completedCount} />
                                <SummaryStat shortLabel="Left" label="Remaining" value={remainingCount} />
                                <SummaryStat shortLabel="Lines" label="Lines" value={completedLines.length} />
                            </dl>
                        </div>
                        <div className="mt-2.5 flex items-center gap-2">
                            <div
                                className="h-2 flex-1 overflow-hidden rounded-full bg-stone-200 shadow-inner sm:h-3"
                                role="progressbar"
                                aria-valuemin={0}
                                aria-valuemax={totalItems}
                                aria-valuenow={completedCount}
                                aria-label={`${completedCount} of ${totalItems} challenges completed`}
                            >
                                <div
                                    className="h-full rounded-full bg-gradient-to-r from-emerald-400 via-green-500 to-teal-500 transition-all duration-700 ease-out"
                                    style={{ width: `${progressPercent}%` }}
                                />
                            </div>
                            <span className="shrink-0 text-xs font-semibold text-emerald-600 sm:text-sm">{Math.round(progressPercent)}%</span>
                        </div>
                    </section>
                )}

                {!loading && boardProblems.length > 0 && (
                    <div
                        role="alert"
                        className="mb-3 rounded-[18px] border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 sm:mb-5 sm:text-sm"
                    >
                        This board is not a complete 5x5 card ({boardProblems.join('; ')}), so lines and the
                        grand prize stay locked until it is repaired.
                    </div>
                )}

                {loading ? (
                    <div className="rounded-[28px] bg-white/80 py-20 text-center text-gray-500 shadow-sm ring-1 ring-stone-200 animate-pulse">
                        Loading Bingo Card...
                    </div>
                ) : (
                    <div className="rounded-[30px] border border-orange-200/70 bg-[#fffaf0] p-2 shadow-[0_24px_80px_rgba(120,53,15,0.12)] backdrop-blur sm:p-4">
                        <div className="mb-1.5 grid grid-cols-5 gap-1.5 sm:mb-2 sm:gap-3">
                            {BINGO_HEADER.map((letter) => (
                                <div key={letter} className="rounded-[14px] border border-orange-200 bg-orange-900 py-1.5 text-center text-base font-black tracking-[0.18em] text-amber-100 shadow-sm sm:py-2 sm:text-2xl">
                                    {letter}
                                </div>
                            ))}
                        </div>
                        <div className="grid grid-cols-5 gap-1.5 sm:gap-3">
                            {items.map((item, index) => {
                                const isCompleted = item.status === 'completed';
                                const isFree = item.id === FREE_ITEM_ID;
                                const tileLabel = getTileLabel(item.text);
                                // The free square is decoration, not a control: a <div>, so it
                                // is not focusable, not clickable and not announced as a button
                                // that does nothing. Same box, so the grid is unchanged.
                                const Tile = isFree ? 'div' : 'button';
                                const interactiveProps = isFree
                                    ? {}
                                    : {
                                        onClick: (e) => handleItemClick(item, e.currentTarget),
                                        // `aria-disabled`, not `disabled`. A real
                                        // `disabled` attribute takes effect the
                                        // instant a save starts -- which is the
                                        // instant the dialog closes and hands focus
                                        // back to this tile. The browser refuses
                                        // focus on a disabled control and drops the
                                        // user at <body>, silently undoing the focus
                                        // restoration the dialog just performed.
                                        // handleItemClick declines the click instead,
                                        // so the tile stays focusable and the
                                        // double-submit guard is unchanged.
                                        'aria-disabled': isSubmitting || undefined,
                                        'aria-label': `${item.text} - ${item.status}`,
                                        'aria-pressed': isCompleted,
                                    };
                                return (
                                <Tile
                                    key={item.id}
                                    {...interactiveProps}
                                    title={item.text}
                                    className={clsx(
                                        "group relative flex min-w-0 flex-col justify-center overflow-hidden rounded-[14px] border px-1.5 py-2 text-center font-semibold leading-none transition-all duration-200 select-none focus:outline-none focus:ring-2 focus:ring-offset-2 animate-fade-in-up sm:rounded-[20px] sm:px-2.5 sm:py-3",
                                        isCompleted
                                            ? 'border-emerald-400 bg-emerald-50 text-emerald-950 shadow-inner'
                                            : activeTheme.tilePending,
                                        isFree && 'border-yellow-300 bg-white font-bold text-yellow-800',
                                        recentlyCompletedId === item.id && 'animate-pop animate-celebrate-pulse',
                                        isInCompletedLine(item.id) && isCompleted && 'ring-2 ring-yellow-400 ring-offset-1'
                                    )}
                                    style={{
                                        aspectRatio: '1 / 1',
                                        animationDelay: `${index * 30}ms`
                                    }}
                                >
                                    <div className="pointer-events-none absolute left-1 top-1 rounded-full bg-orange-50 px-1.5 py-0.5 text-[0.42rem] font-black uppercase tracking-[0.12em] text-orange-900 ring-1 ring-orange-100 sm:text-[0.58rem]">
                                        {String(item.id + 1).padStart(2, '0')}
                                    </div>
                                    {/* Done treatment lives in the corner + bottom edge so it never covers the challenge text */}
                                    {isCompleted && (
                                        <>
                                            <span
                                                className="pointer-events-none absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 text-[0.5rem] font-black leading-none text-white shadow-sm ring-1 ring-white sm:h-5 sm:w-5 sm:text-[0.7rem]"
                                                role="img"
                                                aria-label="completed"
                                            >
                                                ✓
                                            </span>
                                            <span aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-1.5 bg-emerald-400/50" />
                                        </>
                                    )}
                                    <div className="flex flex-1 items-center justify-center pb-1.5 pt-3">
                                        <span
                                            className={clsx(
                                                "block max-w-full break-words leading-[1.06] tracking-[-0.02em] [hyphens:auto]",
                                                getTileTextClass(tileLabel)
                                            )}
                                        >
                                            {tileLabel}
                                        </span>
                                        {isFree && (
                                            <span className="sr-only">Free square, always complete. Not editable.</span>
                                        )}
                                    </div>
                                </Tile>
                                );
                            })}
                        </div>
                        <p className="mt-2 text-center text-[0.7rem] leading-4 text-stone-500 sm:mt-3 sm:text-xs sm:leading-5">
                            Tap any square except FREE to read the full challenge and mark it complete.
                        </p>
                    </div>
                )}

                <div className="mt-4 pb-2 text-center text-sm text-gray-600 italic sm:mt-8 sm:text-base">
                    {isFullBoard ? (
                        <span className="text-xl font-bold text-emerald-600">Congratulations! Full board complete! 🏆</span>
                    ) : (
                        <span>Complete a line to win a &lt;$50 gift. Finish the full board for the grand prize. 🏆</span>
                    )}
                </div>
            </div>

            {/* Completion Modal */}
            {openDialog === 'complete' && (
                <div
                    className={clsx(
                        "fixed inset-0 bg-black flex items-center justify-center p-4 z-50 transition-opacity duration-200",
                        isModalAnimating ? 'bg-opacity-50' : 'bg-opacity-0'
                    )}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="modal-title"
                    onClick={closeModal}
                >
                    <div
                        ref={dialogRef}
                        onKeyDown={handleDialogKeyDown}
                        className={clsx(
                            "bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full transition-all duration-200",
                            isModalAnimating
                                ? 'opacity-100 scale-100 translate-y-0'
                                : 'opacity-0 scale-95 translate-y-4'
                        )}
                        onClick={e => e.stopPropagation()}
                    >
                        <h3 id="modal-title" data-dialog-heading tabIndex={-1} className="text-xl font-bold mb-4 text-gray-900 focus:outline-none">Mark as Complete?</h3>
                        <p className="text-gray-600 mb-4">
                            Did {activePerson === 'kate' ? 'Kate' : 'Nav'} properly complete: <br />
                            <span className="font-bold text-orange-600 block mt-2 text-lg">"{selectedItem.text}"</span>
                        </p>

                        <label htmlFor="completion-date" className="block text-sm font-medium text-gray-700 mb-2">Completion Date:</label>
                        <input
                            id="completion-date"
                            type="date"
                            value={confirmDate}
                            onChange={(e) => setConfirmDate(e.target.value)}
                            className="w-full min-h-[44px] border border-gray-300 rounded-md shadow-sm mb-6 px-3 py-2.5"
                        />

                        <div className="flex space-x-4">
                            <button
                                onClick={closeModal}
                                disabled={isSubmitting}
                                className="flex-1 min-h-[44px] px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:ring-2 focus:ring-gray-300"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmCompletion}
                                disabled={isSubmitting}
                                className="flex-1 min-h-[44px] px-4 py-2.5 bg-green-500 text-white rounded-lg hover:bg-green-600 font-bold focus:ring-2 focus:ring-green-300"
                            >
                                {isSubmitting ? 'Saving...' : 'Confirm'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Manage Entry Modal (formerly Undo Modal) */}
            {openDialog === 'manage' && (
                <div
                    className={clsx(
                        "fixed inset-0 bg-black flex items-center justify-center p-4 z-50 transition-opacity duration-200",
                        isModalAnimating ? 'bg-opacity-50' : 'bg-opacity-0'
                    )}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="manage-modal-title"
                    onClick={closeModal}
                >
                    <div
                        ref={dialogRef}
                        onKeyDown={handleDialogKeyDown}
                        className={clsx(
                            "bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full transition-all duration-200",
                            isModalAnimating
                                ? 'opacity-100 scale-100 translate-y-0'
                                : 'opacity-0 scale-95 translate-y-4'
                        )}
                        onClick={e => e.stopPropagation()}
                    >
                        <h3 id="manage-modal-title" data-dialog-heading tabIndex={-1} className="text-xl font-bold mb-4 text-gray-900 focus:outline-none">Manage Entry</h3>
                        <p className="text-gray-600 mb-4">
                            Item: <span className="font-bold text-orange-600">"{selectedItem.text}"</span>
                        </p>

                        <label htmlFor="manage-date" className="block text-sm font-medium text-gray-700 mb-2">Completion Date:</label>
                        <input
                            id="manage-date"
                            type="date"
                            value={confirmDate}
                            onChange={(e) => setConfirmDate(e.target.value)}
                            className="w-full min-h-[44px] border border-gray-300 rounded-md shadow-sm mb-6 px-3 py-2.5"
                        />

                        <div className="flex flex-col space-y-3">
                            <button
                                onClick={handleConfirmCompletion}
                                disabled={isSubmitting}
                                className="w-full min-h-[44px] px-4 py-2.5 bg-green-500 text-white rounded-lg hover:bg-green-600 font-bold focus:ring-2 focus:ring-green-300"
                            >
                                {isSubmitting ? 'Updating...' : 'Update Date'}
                            </button>

                            <button
                                onClick={handleConfirmUndo}
                                disabled={isSubmitting}
                                className="w-full min-h-[44px] px-4 py-2.5 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 font-medium focus:ring-2 focus:ring-red-300"
                            >
                                Uncheck / Remove
                            </button>

                            <button
                                onClick={closeModal}
                                disabled={isSubmitting}
                                className="w-full min-h-[44px] px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:ring-2 focus:ring-gray-300"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BingoInterface;
